/**
 * WordPress GraphQL Queries
 *
 * 블로그/뉴스/FAQ 데이터 조회를 위한 GraphQL 쿼리 정의
 * blog-news.ts, faq.ts, wordpress-webhook.ts에서 공통으로 사용
 */

// ============================================
// 개별 포스트 상세 쿼리
// ============================================

/**
 * 개별 포스트 상세 쿼리 (slug 기반)
 * - 상세 페이지에서 사용 (blog-news.ts)
 */
export const BLOG_NEWS_ITEM_QUERY_BY_SLUG = `
query getBlogNewsPostBySlug($slug: String!, $categoryId: Int) {
  posts(first: 1, where: { nameIn: [$slug], categoryId: $categoryId }) {
    nodes {
    id
    title
    databaseId
    viewCount
    slug
    content
    modified
    featuredImage {
      node {
        mediaItemUrl
      }
    }
    blog {
      lang
      categoryEn
      categoryKo
      date
      heroimage
      jsonLd
      ogTitle
      ogDescription
      series
      tags
      author2 {
        nodes {
          ... on Post {
            author {
              id
            }
          }
        }
      }
    }
    seo {
      metaDesc
      opengraphImage {
        link
      }
      title
    }
    popup {
      popupButton
      popupEnable
      popupImage
      popupLink
      popupTitle
      popupText
    }
    }
  }
}`;

/**
 * 개별 포스트 상세 쿼리 (databaseId 기반)
 * - Webhook에서 사용 (wordpress-webhook.ts)
 * - 한글 slug 인코딩 문제 해결을 위해 ID로 조회
 */
export const BLOG_NEWS_ITEM_QUERY_BY_ID = `
query getBlogNewsPostById($id: ID!) {
  post(id: $id, idType: DATABASE_ID) {
    id
    title
    databaseId
    viewCount
    slug
    content
    modified
    featuredImage {
      node {
        mediaItemUrl
      }
    }
    blog {
      lang
      categoryEn
      categoryKo
      date
      heroimage
      jsonLd
      ogTitle
      ogDescription
      series
      tags
      author2 {
        nodes {
          ... on Post {
            author {
              id
            }
          }
        }
      }
    }
    seo {
      metaDesc
      opengraphImage {
        link
      }
      title
    }
    popup {
      popupButton
      popupEnable
      popupImage
      popupLink
      popupTitle
      popupText
    }
  }
}`;

// ============================================
// 리스트용 경량 쿼리
// ============================================

/**
 * 리스트용 경량 쿼리
 * - 목록 페이지에서 사용 (카테고리, 검색, 작성자 페이지 등)
 * - content, author details, popup, seo 필드 제외
 */
export const BLOG_NEWS_LIST_QUERY = `
query getBlogNewsPostsList($postCount: Int!, $categoryId: Int!, $after: String) {
  posts(
    first: $postCount,
    where: {
      categoryId: $categoryId,
      status: PUBLISH
    },
    after: $after
  ) {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        id
        title
        databaseId
        viewCount
        slug
        modified
        featuredImage {
          node {
            mediaItemUrl
          }
        }
        blog {
          lang
          categoryEn
          categoryKo
          date
          heroimage
          series
          tags
          author2 {
            nodes {
              ... on Post {
                author {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
}`;

// ============================================
// FAQ 쿼리
// ============================================

/**
 * FAQ 리스트용 경량 쿼리 (content 제외)
 * - 목록/검색 페이지에서 사용
 */
export const FAQ_LIST_QUERY = `
query getFaqItemsList($postCount: Int!, $categoryId: Int!, $after: String) {
  posts(
    first: $postCount,
    where: {
      categoryId: $categoryId,
      status: PUBLISH
    },
    after: $after
  ) {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        id
        title
        slug
        faq {
          lang
          category
        }
      }
    }
  }
}`;

/**
 * FAQ 개별 아이템 상세 쿼리 (slug 기반)
 * - 상세 페이지에서 사용 (faq.ts)
 */
export const FAQ_ITEM_QUERY_BY_SLUG = `
query getFaqItemBySlug($slug: String!, $categoryId: Int) {
  posts(first: 1, where: { nameIn: [$slug], categoryId: $categoryId }) {
    nodes {
      id
      title
      slug
      content
      faq {
        lang
        category
      }
    }
  }
}`;

/**
 * FAQ 개별 아이템 상세 쿼리 (databaseId 기반)
 * - Webhook에서 사용 (wordpress-webhook.ts)
 */
export const FAQ_ITEM_QUERY_BY_ID = `
query getFaqItemById($id: ID!) {
  post(id: $id, idType: DATABASE_ID) {
    id
    title
    slug
    content
    faq {
      lang
      category
    }
  }
}`;
